import React, { useState, useEffect, useRef, useCallback } from "react";
import PageHeader from "../../Routes/PageHeader";
import DataTable from "./DataTable";
import axios from "axios";
import {
  message,
  Modal,
  Select,
  DatePicker,
  Row,
  Col,
  Button,
  Tag,
} from "antd";
import { API, Headers } from "../../Config";
import {
  ReloadOutlined,
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
  ClockCircleOutlined,
  StarOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "./styles.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

function LoyaltyAgentScreen() {
  const [loading, setLoading] = useState(true);
  const [indicator, setIndicator] = useState(false);
  const [page, setPage] = useState(1);
  const [take, setTake] = useState(10);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [filtersVisible, setFiltersVisible] = useState(false);

  const mounted = useRef(false);

  const loadData = useCallback(
    (p, t) => {
      setLoading(true);
      let params = `?page=${p}&take=${t}`;

      if (searchText) params += `&search=${searchText}`;
      if (selectedUser) params += `&userId=${selectedUser}`;
      if (selectedStatus) params += `&status=${selectedStatus}`;
      if (dateRange && dateRange.length === 2) {
        params += `&startDate=${moment(dateRange[0]).format(
          "YYYY-MM-DD"
        )}&endDate=${moment(dateRange[1]).format("YYYY-MM-DD")}`;
      }

      axios
        .get(API.BASR_URL + API.LOYALTY_LIST + params, {
          headers: Headers(),
        })
        .then(function (response) {
          if (mounted.current) {
            if (response.status === 200) {
              setData(
                Array.isArray(response.data.data) ? response.data.data : []
              );
              setMeta(response.data.meta);
            } else {
              message.error("Oops.Something went wrong");
            }
            setLoading(false);
            setIndicator(false);
          }
        })
        .catch(function (error) {
          if (mounted.current) {
            message.error("Oops.Something went wrong");
            setIndicator(false);
            setLoading(false);
          }
        });
    },
    [searchText, selectedUser, selectedStatus, dateRange]
  );

  useEffect(() => {
    mounted.current = true;
    loadData(page, take);
    loadUsers();
    return () => {
      mounted.current = false;
    };
  }, [loadData, page, take]);

  const loadUsers = () => {
    axios
      .get(API.BASR_URL + API.USERS_LIST + "/list", {
        headers: Headers(),
      })
      .then(function (response) {
        if (mounted.current) {
          if (response.status === 200) {
            setUsers(response.data);
          }
        }
      })
      .catch(function (error) {
        if (mounted.current) {
          console.error("Failed to load users:", error);
        }
      });
  };

  const handleUserFilter = (value) => {
    setSelectedUser(value);
    setPage(1);
    loadData(1, take);
  };

  const handleStatusFilter = (value) => {
    setSelectedStatus(value);
    setPage(1);
    loadData(1, take);
  };

  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
    setPage(1);
    loadData(1, take);
  };

  const clearFilters = () => {
    setSearchText("");
    setSelectedUser(null);
    setSelectedStatus(null);
    setDateRange(null);
    setPage(1);
    loadData(1, take);
  };

  const handlePageChange = (page, pageSize) => {
    setPage(page);
    setTake(pageSize);
    loadData(page, pageSize);
  };

  const handleApprove = (id) => {
    Modal.confirm({
      title: "Approve Loyalty Record",
      content: "Are you sure you want to approve this loyalty record?",
      onOk() {
        approveLoyalty(id);
      },
    });
  };

  const approveLoyalty = (id) => {
    setIndicator(true);
    axios
      .patch(
        API.BASR_URL + API.LOYALTY_ACTIONS + `${id}/approve`,
        {},
        {
          headers: Headers(),
        }
      )
      .then(function (response) {
        if (mounted.current) {
          if (response.status === 200) {
            message.success("Loyalty record approved successfully");
            loadData(page, take);
          } else {
            message.error("Failed to approve loyalty record");
          }
          setIndicator(false);
        }
      })
      .catch(function (error) {
        if (mounted.current) {
          message.error("Failed to approve loyalty record");
          setIndicator(false);
        }
      });
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete Loyalty Record",
      content: "Are you sure you want to delete this loyalty record?",
      onOk() {
        deleteLoyalty(id);
      },
    });
  };

  const deleteLoyalty = (id) => {
    setIndicator(true);
    axios
      .delete(API.BASR_URL + API.LOYALTY_ACTIONS + id, {
        headers: Headers(),
      })
      .then(function (response) {
        if (mounted.current) {
          if (response.status === 200) {
            message.success("Loyalty record deleted successfully");
            loadData(page, take);
          } else {
            message.error("Failed to delete loyalty record");
          }
          setIndicator(false);
        }
      })
      .catch(function (error) {
        if (mounted.current) {
          message.error("Failed to delete loyalty record");
          setIndicator(false);
        }
      });
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      pending: { color: "orange", text: "Pending" },
      approved: { color: "green", text: "Approved" },
      rejected: { color: "red", text: "Rejected" },
    };

    const config = statusConfig[status] || { color: "default", text: status };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  const getRedeemableTag = (isRedeemable) => {
    return isRedeemable ? (
      <Tag color="green" icon={<CheckCircleOutlined />}>
        Redeemable
      </Tag>
    ) : (
      <Tag color="red" icon={<CloseCircleOutlined />}>
        Not Redeemable
      </Tag>
    );
  };

  // Calculate statistics
  const calculateStats = () => {
    const stats = {
      total: meta?.itemCount || 0,
      pending: data?.filter((item) => item.status === "pending").length || 0,
      approved: data?.filter((item) => item.status === "approved").length || 0,
      rejected: data?.filter((item) => item.status === "rejected").length || 0,
      redeemed: data?.filter((item) => item.status === "redeemed").length || 0,
      totalPoints:
        data?.reduce((sum, item) => sum + (item.points || 0), 0) || 0,
    };
    return stats;
  };

  const stats = calculateStats();

  return (
    <div className="loyalty-agent-container">
      <PageHeader
        PageName={"Loyalty Agent Management"}
        breadScrum={"Admin / Loyalty Agent Management"}
      />

      <div className="loyalty-agent-card">
        {/* Statistics Cards */}
        <div className="loyalty-agent-stats">
          <div className="loyalty-stat-card">
            <div className="loyalty-stat-icon">
              <DatabaseOutlined />
            </div>
            <div className="loyalty-stat-content">
              <div className="loyalty-stat-number">{stats.total}</div>
              <div className="loyalty-stat-label">Total Records</div>
            </div>
          </div>
          <div className="loyalty-stat-card pending">
            <div className="loyalty-stat-icon">
              <ClockCircleOutlined />
            </div>
            <div className="loyalty-stat-content">
              <div className="loyalty-stat-number">{stats.pending}</div>
              <div className="loyalty-stat-label">Pending Approval</div>
            </div>
          </div>
          <div className="loyalty-stat-card approved">
            <div className="loyalty-stat-icon">
              <CheckCircleOutlined />
            </div>
            <div className="loyalty-stat-content">
              <div className="loyalty-stat-number">{stats.approved}</div>
              <div className="loyalty-stat-label">Approved</div>
            </div>
          </div>
          <div className="loyalty-stat-card rejected">
            <div className="loyalty-stat-icon">
              <CloseCircleOutlined />
            </div>
            <div className="loyalty-stat-content">
              <div className="loyalty-stat-number">{stats.rejected}</div>
              <div className="loyalty-stat-label">Rejected</div>
            </div>
          </div>
          <div className="loyalty-stat-card redeemed">
            <div className="loyalty-stat-icon">
              <GiftOutlined />
            </div>
            <div className="loyalty-stat-content">
              <div className="loyalty-stat-number">{stats.redeemed}</div>
              <div className="loyalty-stat-label">Redeemed</div>
            </div>
          </div>
          <div className="loyalty-stat-card points">
            <div className="loyalty-stat-icon">
              <StarOutlined />
            </div>
            <div className="loyalty-stat-content">
              <div className="loyalty-stat-number">{stats.totalPoints}</div>
              <div className="loyalty-stat-label">Total Points</div>
            </div>
          </div>
        </div>

        <div className="loyalty-agent-filters">
          <Row gutter={[16, 16]} className="loyalty-filter-row">
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by User"
                style={{ width: "100%" }}
                allowClear
                onChange={handleUserFilter}
                value={selectedUser}
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {users.map((user) => (
                  <Option key={user.id} value={user.id}>
                    {user.name || user.email}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Select
                placeholder="Filter by Status"
                style={{ width: "100%" }}
                allowClear
                onChange={handleStatusFilter}
                value={selectedStatus}
              >
                <Option value="pending">Pending</Option>
                <Option value="approved">Approved</Option>
                <Option value="rejected">Rejected</Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <RangePicker
                style={{ width: "100%" }}
                placeholder={["Start Date", "End Date"]}
                onChange={handleDateRangeChange}
                value={dateRange}
                format="YYYY-MM-DD"
              />
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Button
                icon={<ReloadOutlined />}
                onClick={() => {
                  clearFilters();
                  loadData(page, take);
                }}
              >
                Refresh
              </Button>
            </Col>
          </Row>
        </div>

        <div className="loyalty-agent-table">
          <DataTable
            data={data}
            meta={meta}
            loading={loading}
            onPageChange={handlePageChange}
            onApprove={handleApprove}
            onDelete={handleDelete}
            getStatusTag={getStatusTag}
            getRedeemableTag={getRedeemableTag}
          />
        </div>
      </div>
    </div>
  );
}

export default LoyaltyAgentScreen;
